import { toast } from 'react-toastify';
import { useState, useEffect, useMemo } from 'react';
import { CreditAnalysis } from './types';
import ApprovalModal from './ApprovalModal';
import UnifiedDraweeModal from './UnifiedDraweeModal';
import PageFooter from '../../molecules/PageFooter';
import DataTable from './components/DataTable';
import ActionButton from './components/ActionButton';
import { useColumns } from './components/useColumns';
import { saveDecision, getFeatureFlags, listAnalysisItems } from '../../../services/CreditAnalysisService';
import AnalysisColumns from './shared/analysisColumns';
import { exportAnalysisToExcel } from './shared/exportToExcel';
import { parseRangeFilter } from './shared/parseRangeFilter';
import { useDebounce } from './useDebounce';

export default function HistoryView() {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    const [filters, setFilters] = useState<Record<string, string>>(() => {
        const hash = window.location.hash;
        const queryString = hash.includes('?') ? hash.split('?')[1] : '';
        const searchParams = new URLSearchParams(queryString);
        const initial: Record<string, string> = {};
        const batch = searchParams.get('batch');
        const document = searchParams.get('document');
        if (batch) initial.batchNumber = batch;
        if (document) initial.document = document;
        return initial;
    });
    const [sortField, setSortField] = useState('date');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [showFilters, setShowFilters] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAnalysis, setSelectedAnalysis] = useState<CreditAnalysis | null>(null);
    const [analysisData, setAnalysisData] = useState<CreditAnalysis[]>([]);
    const [totalFromApi, setTotalFromApi] = useState(0);
    const [draweeModalDoc, setDraweeModalDoc] = useState<string | null>(null);
    const [draweeModalItemId, setDraweeModalItemId] = useState<string | null>(null);
    const [draweeModalName, setDraweeModalName] = useState<string | null>(null);
    const [draweeModalBatchId, setDraweeModalBatchId] = useState<string | null>(null);
    const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});

    useEffect(() => { getFeatureFlags().then(setFeatureFlags).catch(() => {}); }, []);

    const serverFilterKey = useDebounce(
        JSON.stringify({ b: filters.batchNumber || '', a: filters.assignor || '', d: filters.drawee || '', doc: filters.document || '' })
    );

    useEffect(() => {
        const parsed = JSON.parse(serverFilterKey);
        listAnalysisItems({
            page: currentPage,
            size: currentPageSize,
            batchNumber: parsed.b || undefined,
            assignor: parsed.a || undefined,
            drawee: parsed.d || undefined,
            document: parsed.doc || undefined,
        })
            .then(data => { setAnalysisData((data.content || []) as CreditAnalysis[]); setTotalFromApi(data.totalElements || 0); })
            .catch(() => { toast.error('Erro ao carregar dados'); setAnalysisData([]); });
    }, [currentPage, currentPageSize, serverFilterKey]);



    const { filteredData, totalElements } = useMemo(() => {
        let filtered = [...analysisData];
        const multiselectFields = new Set(['rating', 'recommendation', 'status', 'type']);
        const rangeFields = new Set(['originalValue', 'approvedValue', 'suggestedValue', 'currentExposure', 'potentialExposure', 'scrTotal', 'scrRural', 'overdue', 'scrPrejuizo', 'qsa', 'annualRevenue', 'equity', 'score', 'agroScore', 'pdef', 'pdefAgro', 'scoreQuod', 'scoreBoaVista', 'ccirHa', 'ccirValue', 'patrimonioScr', 'ccirParticipation', 'ccirPatrimSacado', 'ccirPatrimDividaGrupo', 'dupPercent', 'approvedPercent']);
        const displayedInThousands = new Set(['scrTotal', 'scrRural', 'overdue', 'scrPrejuizo', 'qsa', 'ccirPatrimSacado']);
        // Fields stored as 0.0–1.0 fractions but displayed as percentages — filter must match displayed value
        const displayedAsPercent = new Set(['dupPercent', 'approvedPercent']);

        Object.entries(filters).forEach(([field, value]) => {
            if (!value) return;
            if (['batchNumber', 'assignor', 'drawee', 'document'].includes(field)) return; // server-side
            if (rangeFields.has(field)) {
                const predicate = parseRangeFilter(value);
                if (!predicate) return;
                filtered = filtered.filter(item => {
                    const raw = (item as any)[field];
                    let num = Number.parseFloat(String(raw ?? '').replace(/[^\d.,-]/g, '').replace(',', '.'));
                    if (isNaN(num)) return false;
                    if (displayedInThousands.has(field)) num = num / 1000;
                    if (displayedAsPercent.has(field)) num = num * 100;
                    return predicate(num);
                });
                return;
            }
            const vals = value.split(';').map(v => v.trim().toLowerCase()).filter(Boolean);
            const isMultiselect = multiselectFields.has(field);
            filtered = filtered.filter(item => {
                const iv = (item as any)[field];
                if (!iv) return isMultiselect && vals.includes('-');
                const low = String(iv).toLowerCase();
                return isMultiselect ? vals.some(v => v === low) : vals.some(v => low.includes(v));
            });
        });

        if (sortField) {
            filtered.sort((a, b) => {
                const cmp = String((a as any)[sortField] || '').localeCompare(String((b as any)[sortField] || ''), 'pt-BR', { numeric: true });
                return sortOrder === 'ASC' ? cmp : -cmp;
            });
        }

        return { filteredData: filtered, totalElements: totalFromApi };
    }, [filters, sortField, sortOrder, analysisData, totalFromApi]);

    const handleSort = (field: string) => {
        if (sortField === field) setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
        else { setSortField(field); setSortOrder('ASC'); }
    };

    const columns = useColumns<CreditAnalysis>(() => AnalysisColumns({
        onOpenModal: (row) => { setSelectedAnalysis(row); setIsModalOpen(true); },
        onOpenDrawee: (doc, itemId, name, batchId) => { setDraweeModalDoc(doc); setDraweeModalItemId(itemId); setDraweeModalName(name); setDraweeModalBatchId(batchId); },
        onSaveDecision: saveDecision,
        onUpdateRow: (uuid, updates) => setAnalysisData(prev => prev.map(i => i.uuid === uuid ? { ...i, ...updates } as CreditAnalysis : i)),
        featureFlags,
        hideBatchColumn: false,
        showAiColumns: false,
    }), [featureFlags]);

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 h-full flex flex-col">
            {/* Action bar */}
            <div className="mt-4 mb-2 flex items-center justify-end gap-3 p-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ações em Lote:</span>
                <ActionButton variant="secondary" size="sm" onClick={() => setShowFilters(f => !f)}>
                    {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </ActionButton>
                <ActionButton variant="warning" size="sm" onClick={() => setFilters({})}>
                    Limpar Filtros
                </ActionButton>
                <ActionButton variant="success" size="sm" onClick={() => exportAnalysisToExcel(filteredData)}>
                    Exportar Excel
                </ActionButton>
                {filters.batchNumber && (
                    <ActionButton variant="secondary" size="sm">
                        Vincular Número de Contrato
                    </ActionButton>
                )}
            </div>

            <DataTable<CreditAnalysis>
                columns={columns}
                data={filteredData}
                rowKey={(row) => row.uuid}
                filters={filters}
                onFiltersChange={setFilters}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
                showFilters={showFilters}
                showCheckbox={false}
                emptyMessage="Nenhum registro encontrado..."
            />

            <div className="sticky bottom-0 border-t border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 flex-shrink-0 z-10 px-4 py-3">
                <PageFooter
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalElements / currentPageSize)}
                    itemsPerPage={currentPageSize}
                    totalElements={totalElements}
                    onPageChange={(p) => setCurrentPage(p)}
                    onItemsPerPageChange={(ps) => { setCurrentPageSize(ps); setCurrentPage(0); }}
                    itemsLabel="análises de crédito"
                />
            </div>

            {selectedAnalysis && <ApprovalModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} analysis={selectedAnalysis} />}
            {draweeModalDoc && (
                <UnifiedDraweeModal
                    batchId={draweeModalBatchId || ''}
                    itemId={draweeModalItemId || ''}
                    document={draweeModalDoc}
                    draweeName={draweeModalName || undefined}
                    onClose={() => { setDraweeModalDoc(null); setDraweeModalItemId(null); setDraweeModalName(null); setDraweeModalBatchId(null); }}
                />
            )}
        </div>
    );
}
